import React, { useState } from "react";
import { Plus, Loader2, X } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useCategories } from "../../shared/hooks/useCategories";
import { useSnackbar } from "../../shared/hooks/useSnackbar";
import { useCategoryFormSync } from "../../shared/hooks/useCategoryFormSync";
import { CategorySelectors } from "../../shared/components/CategorySelectors";
import { TemplateMetadataFields } from "../../shared/components/TemplateMetadataFields";
import { FormSnackbar } from "../../shared/components/FormSnackbar";
import { parseCommaList, parseFetchError } from "../../shared/utils/formUtils";

interface FormDataType {
  fileKey: string;
  nodeId: string;
  categories: string;
  subCategories: string;
  keywords: string;
  categorySlug: string;
  subcategorySlug: string;
  seoKeywords: string;
  baseName: string;
}

interface UploadError {
  index: number;
  data: FormDataType & { nodeId: string; name: string };
  error: string;
}

const AddCategoryForm = () => {
  const {
    categories,
    loadingCategories,
    selectedCategory,
    selectedSubcategory,
    setCategories,
    setSelectedCategory,
    setSelectedSubcategory,
  } = useCategories();
  
  const {
    showSnackbar,
    snackbarType,
    apiMessage,
    setShowSnackbar,
    showMessage,
  } = useSnackbar();

  const getInitialFormData = (): FormDataType => ({
    fileKey: "",
    nodeId: "",
    categories: "",
    subCategories: "",
    keywords: "",
    categorySlug: "",
    subcategorySlug: "",
    seoKeywords: "",
    baseName: "",
  });

  const [formData, setFormData] = useState<FormDataType>(getInitialFormData());

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    total: number;
    current: number;
    successful: number;
    failed: number;
  }>({ total: 0, current: 0, successful: 0, failed: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);

  useCategoryFormSync(
    categories,
    selectedCategory,
    selectedSubcategory,
    setFormData
  );


  const validateFormData = (data: FormDataType): boolean => {
    // Validate that nodeId contains comma-separated values
    const nodeIds = data.nodeId
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    return (
      data.fileKey.trim() !== "" &&
      data.nodeId.trim() !== "" &&
      nodeIds.length > 0 &&
      data.categories.trim() !== "" &&
      data.subCategories.trim() !== "" &&
      data.keywords.trim() !== "" &&
      data.categorySlug.trim() !== "" &&
      data.subcategorySlug.trim() !== "" &&
      data.seoKeywords.trim() !== "" &&
      data.baseName.trim() !== ""
    );
  };

  const uploadSingleEntry = async (
    data: FormDataType,
    nodeId: string,
    name: string
  ) => {
    const keywords = parseCommaList(data.keywords);
    const seoKeywords = parseCommaList(data.seoKeywords);

    const payload = {
      name: name,
      category: data.categories,
      subcategory: data.subCategories,
      keywords: keywords.join(","),
      categorySlug: data.categorySlug,
      subcategorySlug: data.subcategorySlug,
      seoKeywords: seoKeywords,
    };

    const response = await fetch(
      getApiUrl(
        `/api/templates/v1.0.0/template/fileKey/${data.fileKey}?nodeId=${nodeId}`
      ),
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error(await parseFetchError(response));
    }

    return await response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setShowSnackbar(false);
    setUploadErrors([]);

    // Validate form data
    if (!validateFormData(formData)) {
      setError("Please fill in all required fields");
      showMessage("Please fill in all required fields", "error");
      return;
    }

    // Parse node IDs from comma-separated string
    const nodeIds = formData.nodeId
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    if (nodeIds.length === 0) {
      setError("Please provide at least one valid node ID");
      showMessage("Please provide at least one valid node ID", "error");
      return;
    }

    setIsLoading(true);
    const errors: UploadError[] = [];
    let successCount = 0;

    setUploadProgress({
      total: nodeIds.length,
      current: 0,
      successful: 0,
      failed: 0,
    });

    // Process each node ID
    for (let i = 0; i < nodeIds.length; i++) {
      const nodeId = nodeIds[i];
      const name = `${formData.baseName}${i + 1}`; // Base name + index + 1

      setUploadProgress((prev) => ({ ...prev, current: i + 1 }));

      try {
        await uploadSingleEntry(formData, nodeId, name);
        successCount++;
        setUploadProgress((prev) => ({
          ...prev,
          successful: prev.successful + 1,
        }));
        console.log(
          `✅ Node ${nodeId} uploaded successfully with name: ${name}`
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred";
        errors.push({
          index: i + 1,
          data: { ...formData, nodeId, name },
          error: errorMessage,
        });
        setUploadProgress((prev) => ({ ...prev, failed: prev.failed + 1 }));
        console.error(`❌ Node ${nodeId} failed:`, errorMessage);
      }
    }

    setIsLoading(false);
    setUploadErrors(errors);
    setShowResultsModal(true);

    if (errors.length === 0) {
      // All uploads successful - clear everything
      showMessage(`All ${successCount} templates uploaded successfully!`, "success");
      setFormData(getInitialFormData());
    } else if (successCount > 0) {
      // Partial success - clear form
      showMessage(
        `${successCount} templates uploaded successfully, ${errors.length} failed`,
        "warning"
      );
      setFormData(getInitialFormData());
    } else {
      // All failed - clear form
      showMessage("All uploads failed", "error");
      setFormData(getInitialFormData());
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    // Clear error and success messages when user starts typing
    if (error || success || showSnackbar) {
      setError(null);
      setSuccess(false);
      setShowSnackbar(false);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderFormFields = (data: FormDataType) => {
    const onChange = (field: string, value: string) =>
      handleInputChange(field, value);

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* File Key */}
          <div>
            <label
              htmlFor="fileKey"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              File Key *
            </label>
            <input
              id="fileKey"
              type="text"
              value={data.fileKey}
              onChange={(e) => onChange("fileKey", e.target.value)}
              placeholder="Enter file key"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>

          {/* Node Id */}
          <div>
            <label
              htmlFor="nodeId"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Node Id (comma-separated) *
            </label>
            <input
              id="nodeId"
              type="text"
              value={data.nodeId}
              onChange={(e) => onChange("nodeId", e.target.value)}
              placeholder="e.g., 11-11,11-12,11-21,12-22"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter multiple node IDs separated by commas
            </p>
          </div>
        </div>

        <CategorySelectors
          categories={categories}
          loadingCategories={loadingCategories}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          onCategoryChange={(categoryId) => {
            setSelectedCategory(categoryId);
            setSelectedSubcategory("");
          }}
          onSubcategoryChange={setSelectedSubcategory}
          setCategories={setCategories}
          setLoadingCategories={() => {}}
          onRefreshCategories={() => {}}
        />

        {/* Base Name Input */}
        <div>
          <label
            htmlFor="baseName"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Base Name *
          </label>
          <input
            id="baseName"
            type="text"
            value={data.baseName}
            onChange={(e) => onChange("baseName", e.target.value)}
            placeholder="e.g., template"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
          />
          <p className="text-xs text-gray-500 mt-1">
            Names will be generated as:{" "}
            {data.baseName
              ? `${data.baseName}1, ${data.baseName}2, ${data.baseName}3...`
              : "baseName1, baseName2, baseName3..."}
          </p>
        </div>

        <TemplateMetadataFields
          keywords={data.keywords}
          categorySlug={data.categorySlug}
          subcategorySlug={data.subcategorySlug}
          seoKeywords={data.seoKeywords}
          onKeywordsChange={(v) => onChange("keywords", v)}
          onCategorySlugChange={(v) => onChange("categorySlug", v)}
          onSubcategorySlugChange={(v) => onChange("subcategorySlug", v)}
          onSeoKeywordsChange={(v) => onChange("seoKeywords", v)}
        />
      </>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white my-8 p-8 rounded-2xl shadow-lg border border-gray-100">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">
              Add New Template
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Enter comma-separated node IDs and template details
            </p>
          </div>
          <div className="flex gap-3">
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {renderFormFields(formData)}

          {/* Upload Progress */}
          {isLoading && (
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="flex justify-between text-sm text-gray-700 mb-2">
                <span>
                  Uploading: {uploadProgress.current} / {uploadProgress.total}
                </span>
                <span>
                  ✓ {uploadProgress.successful} | ✗ {uploadProgress.failed}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{
                    width: `${
                      (uploadProgress.current / uploadProgress.total) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 shadow-lg ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>
                  Uploading Templates... ({uploadProgress.current}/
                  {uploadProgress.total})
                </span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span>Upload Templates</span>
              </>
            )}
          </button>
        </form>
      </div>

      <FormSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setError(null);
          setSuccess(false);
        }}
        type={snackbarType}
        error={error}
        apiMessage={apiMessage}
        successMessage="Template added successfully! 🎉"
      />

      {/* Results Modal for Multiple Upload */}
      {showResultsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  Upload Results
                </h3>
                <button
                  onClick={() => setShowResultsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
              <div className="mt-4 flex gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span>Successful: {uploadProgress.successful}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  <span>Failed: {uploadProgress.failed}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                  <span>Total: {uploadProgress.total}</span>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {uploadErrors.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎉</div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">
                    All Uploads Successful!
                  </h4>
                  <p className="text-gray-600">
                    All {uploadProgress.total} templates were uploaded
                    successfully.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    Failed Uploads ({uploadErrors.length})
                  </h4>
                  {uploadErrors.map((error, idx) => (
                    <div
                      key={`error-${error.index}-${idx}`}
                      className="border border-red-200 bg-red-50 rounded-xl p-4"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h5 className="font-semibold text-red-800">
                          Entry #{error.index} - {error.data.name || "Unnamed"}
                        </h5>
                        <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded">
                          Failed
                        </span>
                      </div>
                      <p className="text-sm text-red-700 mb-3">
                        <strong>Error:</strong> {error.error}
                      </p>
                      <div className="bg-white rounded-lg p-3 text-xs space-y-1">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-gray-500">File Key:</span>{" "}
                            <span className="font-mono">
                              {error.data.fileKey}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Node ID:</span>{" "}
                            <span className="font-mono">
                              {error.data.nodeId}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Name:</span>{" "}
                            <span className="font-mono">{error.data.name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Category:</span>{" "}
                            <span className="font-mono">
                              {error.data.categories}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowResultsModal(false)}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCategoryForm;
